import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row } from 'antd'

import {
  organizationsAccessSessionsListOptions,
  organizationsAccessSignInActivityRetrieveOptions
} from 'src/api/main'

import { useUserAppAccess } from '../hooks/useUserAppAccess'
import type { WorkspaceUser } from '../types'

import { UserAuthenticationCard } from './UserAuthenticationCard'
import { UserIdentityCard } from './UserIdentityCard'
import { UserQuickStats } from './UserQuickStats'
import { UserRecentActivityCard } from './UserRecentActivityCard'
import { UserWorkspaceAccessCard } from './UserWorkspaceAccessCard'

type Props = {
  record: WorkspaceUser
  workspaceId: number
  onEdit: () => void
  onResetPassword: () => void
  onRequireMfa: () => void
  onDisableMfa: () => void
  onResetMfa: () => void
  onViewAudit: () => void
  /** False when the viewer holds `users.view` but not `users.manage`. */
  canManage?: boolean
  /** Recent activity and sign-in metrics need `audit.view`. */
  canViewAudit?: boolean
  /** The apps-used metric reads the app list — `applications.view`. */
  canViewApps?: boolean
}

export const UserDetailOverviewTab = ({
  record,
  workspaceId,
  onEdit,
  onResetPassword,
  onRequireMfa,
  onDisableMfa,
  onResetMfa,
  onViewAudit,
  canManage = true,
  canViewAudit = true,
  canViewApps = true
}: Props) => {
  const { grantedCount } = useUserAppAccess({
    workspaceId,
    userId: record.user.id,
    enabled: canViewApps
  })

  // Active sessions and trailing-30d sign-ins reuse the same per-member
  // endpoints the Sessions tab and audit log expose; the count + metric feed
  // the quick-stats header.
  // Each metric's endpoint carries its own code, so a member without it must
  // not fire the request either — it would only 403.
  const { data: sessions } = useQuery({
    ...organizationsAccessSessionsListOptions({ path: { id: record.id } }),
    enabled: record.id > 0 && canManage
  })

  const { data: signInActivity } = useQuery({
    ...organizationsAccessSignInActivityRetrieveOptions({
      path: { id: record.id }
    }),
    enabled: record.id > 0 && canViewAudit
  })

  return (
    <Row gutter={14}>
      <Col xs={24} lg={16}>
        <Flex vertical gap={14}>
          <UserIdentityCard
            user={record.user}
            jobTitle={record.job_title}
            onEdit={canManage ? onEdit : undefined}
          />
          {/* Workspace access carries the member's role and the inline
              "change role" popover. The separate multi-select "Workspace roles"
              card is gone: a member holds one workspace role, so offering a
              second, multi-value editor for the same field invited exactly the
              drift it was meant to manage. */}
          <UserWorkspaceAccessCard
            record={record}
            workspaceId={workspaceId}
            editable={canManage}
          />
        </Flex>
      </Col>

      <Col xs={24} lg={8}>
        <Flex vertical gap={14}>
          <UserQuickStats
            appsUsed={grantedCount}
            sessions={sessions?.length}
            signIns30d={signInActivity?.sign_ins}
            showAppsUsed={canViewApps}
            showSessions={canManage}
            showSignIns={canViewAudit}
          />
          <UserAuthenticationCard
            record={record}
            canManage={canManage}
            onResetPassword={onResetPassword}
            onRequireMfa={onRequireMfa}
            onDisableMfa={onDisableMfa}
            onResetMfa={onResetMfa}
          />
          {canViewAudit ? (
            <UserRecentActivityCard
              workspaceId={workspaceId}
              userId={record.user.id}
              onSeeAll={onViewAudit}
            />
          ) : null}
        </Flex>
      </Col>
    </Row>
  )
}
